import {
  Navbar,
  NavbarBrand,
  NavbarContent,
  NavbarItem,
  NavbarMenu,
  NavbarMenuToggle,
  Popover,
  PopoverContent,
  PopoverTrigger,
  Button,
  Switch,
  Dropdown,
  DropdownTrigger,
  DropdownMenu,
  DropdownItem,
  Accordion,
  AccordionItem,
} from "@heroui/react";
import { YallyLogo } from "assets/index.ts";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { categorizedRoutesConfig, standaloneRoutes, type RouteItem, type RouteCategory } from "shared/config/routes.tsx";
import React, { useContext, useState, useEffect, useMemo } from "react";
import { usePermissions } from "../../contexts/permissions.context";
import { getMobileSubmenuLinkStyles } from "./navMenu";
import { useSession, useUser } from "@clerk/clerk-react";
import { API_URL } from "constants/index";
import { fetchWithAuth } from "shared/api/fetch-with-auth";

import SearchComponent from "../searchBar/components/searchComponent";
import { MenuContext } from "shared/components/menuProvider/menuProvider.tsx";
import {
  ArrowSquareOut,
  Bell,
  Buildings,
  CaretLeft,
  CheckSquare,
  FileArrowUp,
  FileCloud,
  Image,
  List,
  MagnifyingGlass,
  Money,
  Plus,
  Signature,
  User,
  Umbrella,
  WhatsappLogo,
  EnvelopeSimple,
  X,
  SirenIcon,
} from "@phosphor-icons/react";

export const MobileNavMenu = () => {
  const {
    dispatch,
    onOpenAcquisition,
    openDocumentUpload,
    openPhotoUpload,
  } = useContext(MenuContext);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isSearchActive, setIsSearchActive] = useState(false);
  const location = useLocation();
  const isChildPage = location.pathname.split("/").length > 2;
  const navigate = useNavigate();
  const { hasPermission, isLoading } = usePermissions();
  const { session } = useSession();
  const { user } = useUser();
  
  // Notifications state
  const [notifications, setNotifications] = useState<any[]>([]);
  const [notifOpen, setNotifOpen] = useState(false);
  const [showOnlyUnread, setShowOnlyUnread] = useState(true);
  const [unreadCount, setUnreadCount] = useState(0);
  
  // Search state
  // const [searchOpen, setSearchOpen] = useState(false);

  // Notifications logic (extracted from searchBar)
  const displayedNotifications = notifications
    .filter((notif) => (showOnlyUnread ? !notif.readByUsers?.includes(user?.id) : true));

  // Fetch notifications
  useEffect(() => {
    const fetchNotifications = async () => {
      if (!session || !user?.id) return;
      try {
        const res = await fetchWithAuth(`${API_URL}/notifications/get-notifications`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ userId: user.id }),
        }, session);
        if (res.ok) {
          const data = await res.json();
          setNotifications(data);
        }
      } catch (err) {
        console.error("Error fetching notifications:", err);
      }
    };
    fetchNotifications();
    const intervalId = setInterval(fetchNotifications, 60000);
    return () => clearInterval(intervalId);
  }, [session, user?.id]);

  // Update unread count
  useEffect(() => {
    setUnreadCount(notifications.filter((notif) => !notif.readByUsers?.includes(user?.id)).length);
  }, [notifications, user?.id]);

  const markAsRead = async (id: number) => {
    if (!session || !user?.id) return;
    try {
      const res = await fetchWithAuth(`${API_URL}/notifications/mark-as-read/${id}`, {
        method: "POST",
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ userId: user.id }),
      }, session);
      if (res.ok) {
        setNotifications((prev: any[]) =>
          prev.map((n) => (n.id === id ? { ...n, readByUsers: [...(n.readByUsers || []), user.id] } : n))
        );
      }
    } catch (err) {
      console.error("Error marking notification as read", err);
    }
  };

  const markAllAsRead = async () => {
    if (!session || !user?.id) return;
    try {
      await fetchWithAuth(`${API_URL}/notifications/mark-all-as-read`, {
        method: "POST",
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ userId: user.id }),
      }, session);
      setNotifications((prev: any[]) =>
        prev.map((n) => ({ ...n, readByUsers: [...(n.readByUsers || []), user.id] }))
      );
    } catch (err) {
      console.error("Error marking all notifications as read", err);
    }
  };

  const handleOpenEsign = () => {
    window.location.href = `https://portal.yally.be/web#action=227&model=sign.request&view_type=kanban&menu_id=155&cids=1%2C2%2C3%2C4`;
  };

  // Filter categorized routes based on permissions
  const filteredCategorizedRoutes = useMemo(() => {
    if (isLoading) return [];

    const filterRoutes = (routes: RouteItem[]): RouteItem[] => {
      return routes.reduce((acc: RouteItem[], route) => {
        const hasMainPermission = !route.permission || hasPermission(route.permission as any);
        if (!hasMainPermission) return acc;
        if (route.showInNav === false) return acc;

        let filteredChildren: RouteItem[] | undefined;
        if (route.children) {
          filteredChildren = filterRoutes(route.children);
          filteredChildren = filteredChildren.length > 0 ? filteredChildren : undefined;
        }

        if (hasMainPermission) {
          acc.push({
            ...route,
            children: filteredChildren
          });
        }

        return acc;
      }, []);
    };

    return categorizedRoutesConfig.reduce((acc: RouteCategory[], category) => {
      // Skip categories that are explicitly hidden
      if (category.showInNav === false) return acc;
      
      const filteredRoutes = filterRoutes(category.routes);
      if (filteredRoutes.length > 0) {
        acc.push({
          ...category,
          routes: filteredRoutes
        });
      }
      return acc;
    }, []);
  }, [hasPermission, isLoading]);

  // Filter standalone routes based on permissions
  const filteredStandaloneRoutes = useMemo(() => {
    if (isLoading) return [];
    
    const filterRoutes = (routes: RouteItem[]): RouteItem[] => {
      return routes.reduce((acc: RouteItem[], route) => {
        const hasMainPermission = !route.permission || hasPermission(route.permission as any);
        if (!hasMainPermission) return acc;

        let filteredChildren: RouteItem[] | undefined;
        if (route.children) {
          filteredChildren = filterRoutes(route.children);
          filteredChildren = filteredChildren.length > 0 ? filteredChildren : undefined;
        }

        if (hasMainPermission) {
          acc.push({
            ...route,
            children: filteredChildren
          });
        }

        return acc;
      }, []);
    };

    return filterRoutes(standaloneRoutes);
  }, [hasPermission, isLoading]);

  // Helper function to match route patterns with parameters (e.g., /building/:id)
  const matchesRoute = (routePath: string, currentPath: string): boolean => {
    // Exact match
    if (routePath === currentPath) return true;
    
    // Pattern matching for dynamic segments (e.g., :id, :buildingId)
    const routeParts = routePath.split('/');
    const currentParts = currentPath.split('/');
    
    if (routeParts.length !== currentParts.length) return false;
    
    return routeParts.every((part, index) => {
      // Match dynamic segments (starts with :)
      if (part.startsWith(':')) return true;
      // Match exact segments
      return part === currentParts[index];
    });
  };

  // Find current category or route with subitems (same logic as desktop)
  const currentSubnavConfig = useMemo(() => {
    const currentPath = location.pathname;
    
    // First, check if we're in a category (show all routes in that category)
    for (const category of categorizedRoutesConfig) {
      const isInCategory = category.routes.some(route => 
        matchesRoute(route.to, currentPath) || 
        (route.children && route.children.some(child => matchesRoute(child.to, currentPath)))
      );
      
      if (isInCategory && category.routes.length > 1) {
        // Return a pseudo-route that represents the category
        return {
          type: 'category' as const,
          label: category.label,
          icon: category.icon,
          routes: category.routes
        };
      }
    }
    
    // Find the route that matches the current path and has children
    const findRouteWithChildren = (routes: RouteItem[]): RouteItem | null => {
      for (const route of routes) {
        // Check if current path matches this route and it has children
        if (matchesRoute(route.to, currentPath) && route.children && route.children.length > 0) {
          return route;
        }
        
        // Also check if we're on a child route, then return the parent
        if (route.children) {
          const hasChildMatch = route.children.some(child => matchesRoute(child.to, currentPath));
          if (hasChildMatch) {
            return route;
          }
        }
      }
      return null;
    };
    
    // Search in ALL categorized routes (including hidden ones) for subnav detection
    for (const category of categorizedRoutesConfig) {
      const found = findRouteWithChildren(category.routes);
      if (found) return { type: 'route' as const, route: found };
    }
    
    // Search in standalone routes
    const foundStandalone = findRouteWithChildren(standaloneRoutes);
    if (foundStandalone) return { type: 'route' as const, route: foundStandalone };
    
    return null;
  }, [location.pathname]);

  const handleBackClick = () => {
    navigate(-1); // Go back to the previous page
  };

  useEffect(() => {
    if (localStorage.getItem("navigateToContact") === "true") {
      localStorage.removeItem("navigateToContact");
      navigate("/contact/new");
    }
  }, [navigate]);

  const handleSelect = () => {
    setIsSearchActive(false);
  };

  return (
    <div
      id="navBar"
      className="z-60 mb-24 w-full divide-y divide-[#000000]/15"
    >
      <Navbar
        isMenuOpen={isMenuOpen}
        onMenuOpenChange={setIsMenuOpen}
        position="sticky"
        isBordered
        classNames={{
          base: "fixed bg-muted shadow-md backdrop-blur-lg",
          wrapper: "px-3",
          toggle: "w-fit outline-hidden",
        }}
      >
        <NavbarContent className="gap-2.5 sm:hidden" justify="start">
          {isChildPage && (
            <div className="flex items-center rounded-full border border-muted-foreground/50 p-2 text-sm font-medium outline-hidden">
              <a href="#" onClick={handleBackClick}>
                <CaretLeft weight="bold" size={22} />
              </a>
            </div>
          )}
          <div className="flex items-center rounded-full border border-muted-foreground/50 p-2 text-sm font-medium outline-hidden">
            <NavbarMenuToggle
              icon={
                isMenuOpen ? (
                  <X weight="bold" size={22} />
                ) : (
                  <List weight="bold" size={22} />
                )
              }
            />
          </div>
        </NavbarContent>
        <NavbarContent className="sm:hidden" justify="center">
          <NavbarBrand>
            <a href="/">
              <YallyLogo />
            </a>
          </NavbarBrand>
        </NavbarContent>
        <NavbarContent justify="end">
          <NavbarItem>
            <div className="flex items-center gap-1.5">
              <div className="flex items-center rounded-full border border-muted-foreground/50 p-2 text-sm font-medium">
                <a
                  href="#"
                  className="flex cursor-pointer items-center outline-hidden"
                  onClick={() => setIsSearchActive(!isSearchActive)}
                >
                  <MagnifyingGlass weight="bold" size={22} />
                </a>
              </div>
              
              {/* Notifications */}
              <div className="flex items-center rounded-full border border-muted-foreground/50 p-2 text-sm font-medium">
                <Popover isOpen={notifOpen} onOpenChange={setNotifOpen} placement="bottom" offset={10}>
                  <PopoverTrigger>
                    <button className="relative flex cursor-pointer items-center outline-hidden">
                      <Bell weight="bold" size={22} />
                      {unreadCount > 0 && (
                        <span className="absolute -top-2 -right-2 z-10 flex items-center justify-center min-w-[16px] h-[16px] rounded-full bg-red-500 text-white text-xs">
                          {unreadCount > 9 ? "9+" : unreadCount}
                        </span>
                      )}
                    </button>
                  </PopoverTrigger>
                  <PopoverContent>
                    <div className="p-2 w-80 max-w-[90vw]">
                      <div className="flex items-center justify-between mb-2 space-x-2">
                        <span className="text-sm font-medium">
                          {showOnlyUnread ? "Alleen ongelezen" : "Alle notificaties"}
                        </span>
                        <Switch
                          size="sm"
                          isSelected={showOnlyUnread}
                          onChange={(e) => setShowOnlyUnread(e.target.checked)}
                        />
                        <Button 
                          size="sm"
                          className="text-xs bg-moss-primary text-white hover:bg-moss-primary/90" 
                          onClick={markAllAsRead}
                        >
                          Alles gelezen
                        </Button>
                      </div>
                      <ul className="max-h-60 overflow-y-auto">
                        {displayedNotifications.length === 0 ? (
                          <li className="p-2 text-center text-sm text-gray-500">
                            Geen notificaties
                          </li>
                        ) : (
                          displayedNotifications.map((notif) => (
                            <li
                              key={notif.id}
                              className={`p-2 border-b last:border-0 cursor-pointer ${
                                notif.readByUsers?.includes(user?.id) ? "bg-gray-100" : "bg-muted"
                              }`}
                              onClick={() => {
                                if (!notif.readByUsers?.includes(user?.id)) markAsRead(notif.id);
                              }}
                            >
                              <div className={notif.readByUsers?.includes(user?.id) ? "text-gray-500" : "text-black font-bold"}>
                                {notif.url ? (
                                  <div className="flex items-center">
                                    <span>{notif.message}</span>
                                    <a
                                      href={notif.url}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      onClick={(e) => e.stopPropagation()}
                                      className="ml-2 inline-flex items-center justify-center bg-moss-primary rounded-full p-1"
                                    >
                                      <ArrowSquareOut size={14} weight="duotone" className="text-white" />
                                    </a>
                                  </div>
                                ) : (
                                  notif.message
                                )}
                              </div>
                              <div className="text-xs text-gray-500">
                                {new Date(notif.createdAt).toLocaleString()}
                              </div>
                              {!notif.readByUsers?.includes(user?.id) && (
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    markAsRead(notif.id);
                                  }}
                                  className="text-xs text-brick-primary mt-1"
                                >
                                  Markeer als gelezen
                                </button>
                              )}
                      </li>
                          ))
                        )}
                      </ul>
                    </div>
                  </PopoverContent>
                </Popover>
              </div>

              {/* Tools Dropdown */}
              <div className="flex items-center rounded-full border border-neutral-primary/20 p-2 text-sm font-medium">
                <Dropdown>
                  <DropdownTrigger>
                    <button className="flex cursor-pointer items-center outline-hidden">
                      <Money weight="bold" size={22} />
                    </button>
                  </DropdownTrigger>
                  <DropdownMenu aria-label="Tools menu">
                    <DropdownItem key="klantenfacturen" textValue="Klantenfacturen" className="p-0">
                      <a 
                        href="https://portal.yally.be/web#action=270&model=account.move&view_type=list&menu_id=291&cids=1%2C2%2C3%2C4" 
                        target="_blank"
                        className="flex items-center gap-2 w-full p-2"
                      >
                        <Money size={16} weight="duotone" />
                        Klantenfacturen
                      </a>
                    </DropdownItem>
                    <DropdownItem key="leveranciersfacturen" textValue="Leveranciersfacturen" className="p-0">
                      <a 
                        href="https://portal.yally.be/web#action=272&model=account.move&view_type=list&cids=1%2C3%2C2%2C4&menu_id=291" 
                        target="_blank"
                        className="flex items-center gap-2 w-full p-2"
                      >
                        <Money size={16} weight="duotone" />
                        Leveranciersfacturen
                      </a>
                    </DropdownItem>
                    <DropdownItem key="streetwisebelfius" textValue="Belfius - Streetwise NV" className="p-0">
                      <a 
                        href="https://portal.yally.be/web#cids=1%2C3%2C2%2C4&menu_id=291&active_id=14&model=account.bank.statement.line&view_type=kanban" 
                        target="_blank"
                        className="flex items-center gap-2 w-full p-2"
                      >
                        <Money size={16} weight="duotone" />
                        Belfius - Streetwise NV
                      </a>
                    </DropdownItem>
                    <DropdownItem key="immoalfabelfius" textValue="Belfius - Immo Alfa NV" className="p-0">
                      <a 
                        href="https://portal.yally.be/web#menu_id=291&cids=1%2C3%2C2%2C4&active_id=21&model=account.bank.statement.line&view_type=kanban" 
                        target="_blank"
                        className="flex items-center gap-2 w-full p-2"
                      >
                        <Money size={16} weight="duotone" />
                        Belfius - Immo Alfa NV
                      </a>
                    </DropdownItem>
                    <DropdownItem key="contracten" textValue="Huurcontracten" className="p-0">
                      <a 
                        href="https://portal.yally.be/web#menu_id=369&cids=1%2C3%2C2%2C4&action=1217&model=sale.order&view_type=kanban" 
                        target="_blank"
                        className="flex items-center gap-2 w-full p-2"
                      >
                        <Money size={16} weight="duotone" />
                        Huurcontracten
                      </a>
                    </DropdownItem>
                    <DropdownItem key="ledger" textValue="Grootboekschema" className="p-0">
                      <a 
                        href="https://portal.yally.be/web#action=280&model=account.account&view_type=list&menu_id=291&cids=1%2C3%2C2%2C4" 
                        target="_blank"
                        className="flex items-center gap-2 w-full p-2"
                      >
                        <Money size={16} weight="duotone" />
                        Grootboekschema
                      </a>
                    </DropdownItem>
                    <DropdownItem key="managerial-pl" textValue="Managerial Reporting - P&L" className="p-0">
                      <a 
                        href="https://portal.yally.be/web#menu_id=291&cids=1%2C2%2C3%2C4&action=1263" 
                        target="_blank"
                        className="flex items-center gap-2 w-full p-2"
                      >
                        <Money size={16} weight="duotone" />
                        Managerial Reporting - P&L
                      </a>
                    </DropdownItem>
                    <DropdownItem key="managerial-balans" textValue="Managerial Reporting - Balans" className="p-0">
                      <a 
                        href="https://portal.yally.be/web#menu_id=291&cids=1%2C2%2C3%2C4&action=1264" 
                        target="_blank"
                        className="flex items-center gap-2 w-full p-2"
                      >
                        <Money size={16} weight="duotone" />
                        Managerial Reporting - Balans
                      </a>
                    </DropdownItem>
                    <DropdownItem key="esign" textValue="E-Sign">
                      <button onClick={handleOpenEsign} className="flex items-center gap-2 w-full">
                        <Signature size={16} weight="duotone" />
                        E-Sign
                      </button>
                    </DropdownItem>
                  </DropdownMenu>
                </Dropdown>
              </div>

              {/* Voeg toe Dropdown */}
              <div className="flex items-center rounded-full border border-neutral-primary/20 p-2 text-sm font-medium">
                <Dropdown>
                  <DropdownTrigger>
                    <button className="flex cursor-pointer items-center outline-hidden">
                      <Plus weight="bold" size={22} />
                    </button>
                  </DropdownTrigger>
                  <DropdownMenu 
                    aria-label="Voeg toe menu"
                    onAction={(key) => {
                      switch (key) {
                        case "acquisitie":
                          onOpenAcquisition();
                          break;
                        case "helpdeskticket":
                          dispatch({ type: "helpdeskModal", payload: true });
                          break;
                        case "communicatie":
                          dispatch({ type: "startCommunicationModal", payload: true });
                          break;
                        case "verzekeringsdossier":
                          dispatch({ type: "addInsuranceCaseModal", payload: true });
                          break;
                        case "taak":
                          dispatch({ type: "taskModal", payload: true });
                          break;
                        case "contact":
                          navigate(`/contact/new?fresh=${Date.now()}`);
                          break;
                        case "bedrijf":
                          navigate(`/company/new`);
                          break;
                        case "upload-document":
                          openDocumentUpload();
                          break;
                        case "genereer-document":
                          navigate("/document-generator");
                          break;
                        case "mass-mail":
                          dispatch({ type: "massMailModal", payload: true });
                          break;
                        case "foto":
                          openPhotoUpload();
                          break;
                      }
                    }}
                  >
                    <DropdownItem key="acquisitie" textValue="Acquisitie">
                      <div className="flex items-center gap-2">
                        <Buildings size={16} weight="duotone" />
                        Acquisitie
                      </div>
                    </DropdownItem>
                    <DropdownItem key="helpdeskticket" textValue="Helpdeskticket">
                      <div className="flex items-center gap-2">
                        <SirenIcon size={16} weight="duotone" />
                        Helpdeskticket
                      </div>
                    </DropdownItem>
                    <DropdownItem key="communicatie" textValue="Communicatie">
                      <div className="flex items-center gap-2">
                        <WhatsappLogo size={16} weight="duotone" />
                        Communicatie
                      </div>
                    </DropdownItem>
                    <DropdownItem key="verzekeringsdossier" textValue="Verzekeringsdossier">
                      <div className="flex items-center gap-2">
                        <Umbrella size={16} weight="duotone" />
                        Verzekeringsdossier
                      </div>
                    </DropdownItem>
                    <DropdownItem key="taak" textValue="Taak">
                      <div className="flex items-center gap-2">
                        <CheckSquare size={16} weight="duotone" />
                        Taak
                      </div>
                    </DropdownItem>
                    <DropdownItem key="contact" textValue="Contact">
                      <div className="flex items-center gap-2">
                        <User size={16} weight="duotone" />
                          Contact
                      </div>
                    </DropdownItem>
                    <DropdownItem key="bedrijf" textValue="Bedrijf">
                      <div className="flex items-center gap-2">
                        <Buildings size={16} weight="duotone" />
                        Bedrijf
                      </div>
                    </DropdownItem>
                    <DropdownItem key="upload-document" textValue="Upload document">
                      <div className="flex items-center gap-2">
                        <FileArrowUp size={16} weight="duotone" />
                        Upload document
                      </div>
                    </DropdownItem>
                    <DropdownItem key="genereer-document" textValue="Genereer document">
                      <div className="flex items-center gap-2">
                        <FileCloud size={16} weight="duotone" />
                        Genereer document
                      </div>
                    </DropdownItem>
                    <DropdownItem key="mass-mail" textValue="Mass-mail">
                      <div className="flex items-center gap-2">
                        <EnvelopeSimple size={16} weight="duotone" />
                        Mass-mail
                      </div>
                    </DropdownItem>
                    <DropdownItem key="foto" textValue="Foto">
                      <div className="flex items-center gap-2">
                        <Image size={16} weight="duotone" />
                        Foto
                      </div>
                    </DropdownItem>
                  </DropdownMenu>
                </Dropdown>
              </div>
              {/* <Dropdown backdrop="opaque">
                <DropdownTrigger>
                  <a href="#" className="outline-hidden">
                    <Plus className="h-[22px] w-[22px] text-neutral-primary" />
                  </a>
                </DropdownTrigger>
                <DropdownMenu aria-label="Static Actions">
                  <DropdownItem
                    onClick={(e) =>
                      handleMenuItemClick(e, handleKayleeOpenChange)
                    }
                    onTouchStart={(e) =>
                      handleMenuItemClick(e, handleKayleeOpenChange)
                    }
                    role="button"
                    startContent={
                      <ReadMessageRound className="text-moss-primary" />
                    }
                    key="KayleeBeta"
                  >
                    Ella beta{" "}
                  </DropdownItem>
                  <DropdownItem
                    onClick={(e) =>
                      handleMenuItemClick(e, handleTenantKayleeOpenChange)
                    }
                    onTouchStart={(e) =>
                      handleMenuItemClick(e, handleTenantKayleeOpenChange)
                    }
                    role="button"
                    startContent={
                      <ReadMessageRound className="text-moss-primary" />
                    }
                    key="KayleeBetaHelpdesk"
                  >
                    {" "}
                    Ella beta (Helpdesk Assistant){" "}
                  </DropdownItem>
                  <DropdownItem
                    onClick={(e) => handleMenuItemClick(e, onOpenAcquisition)}
                    onTouchStart={(e) =>
                      handleMenuItemClick(e, onOpenAcquisition)
                    }
                    role="button"
                    startContent={<Building className="text-moss-primary" />}
                    key="Acquisitie"
                  >
                    Acquisitie
                  </DropdownItem>
                  <DropdownItem
                    role="button"
                    startContent={
                      <AlertQuestion className="text-moss-primary" />
                    }
                    key="Helpdeskticket"
                  >
                    Helpdeskticket
                  </DropdownItem>
                  <DropdownItem
                    role="button"
                    onClick={(e) =>
                      handleMenuItemClick(e, () =>
                        dispatch({ type: "taskModal", payload: true }),
                      )
                    }
                    onTouchStart={(e) =>
                      handleMenuItemClick(e, () =>
                        dispatch({ type: "taskModal", payload: true }),
                      )
                    }
                    startContent={
                      <CheckmarkCircle className="text-moss-primary" />
                    }
                    key="Taak"
                  >
                    Taak
                  </DropdownItem>
                  <DropdownItem
                    role="button"
                    onClick={(e) =>
                      handleMenuItemClick(e, () =>
                        dispatch({ type: "documentModal", payload: true }),
                      )
                    }
                    onTouchStart={(e) =>
                      handleMenuItemClick(e, () =>
                        dispatch({ type: "documentModal", payload: true }),
                      )
                    }
                    startContent={<Document className="text-moss-primary" />}
                    key="UploadDocument"
                  >
                    Upload document
                  </DropdownItem>
                  <DropdownItem
                    role="button"
                    onClick={(e) =>
                      handleMenuItemClick(e, () =>
                        dispatch({
                          type: "generateDocumentModal",
                          payload: true,
                        }),
                      )
                    }
                    onTouchStart={(e) =>
                      handleMenuItemClick(e, () =>
                        dispatch({
                          type: "generateDocumentModal",
                          payload: true,
                        }),
                      )
                    }
                    startContent={<Document className="text-moss-primary" />}
                    key="GenerateDocument"
                  >
                    Genereer document
                  </DropdownItem>
                  <DropdownItem
                    role="button"
                    onClick={(e) =>
                      handleMenuItemClick(e, () =>
                        dispatch({ type: "photoUploadModal", payload: true }),
                      )
                    }
                    onTouchStart={(e) =>
                      handleMenuItemClick(e, () =>
                        dispatch({ type: "photoUploadModal", payload: true }),
                      )
                    }
                    startContent={<Photos className="text-moss-primary" />}
                    key="foto"
                  >
                    Foto
                  </DropdownItem>
                  <DropdownItem
                    role="button"
                    onClick={(e) =>
                      handleMenuItemClick(e, () => {
                        localStorage.setItem("navigateToContact", "true");
                        window.location.reload();
                      })
                    }
                    onTouchStart={(e) =>
                      handleMenuItemClick(e, () => {
                        localStorage.setItem("navigateToContact", "true");
                        window.location.reload();
                      })
                    }
                    startContent={<Users className="text-moss-primary" />}
                    key="contact"
                  >
                    Contact
                  </DropdownItem>
                </DropdownMenu>
              </Dropdown> */}
            </div>
          </NavbarItem>
        </NavbarContent>
        <NavbarMenu className="z-50 h-fit p-0 text-foreground shadow-md overflow-y-auto max-h-[80vh]">
          <div className="py-2">
            {/* Render categorized routes as accordion */}
            <Accordion 
              variant="light"
              selectionMode="multiple"
              className="px-0"
            >
              {filteredCategorizedRoutes.map((category) => {
                // Check if any route in this category is active
                const isCategoryActive = category.routes.some(route => 
                  location.pathname === route.to || 
                  (route.children && route.children.some(child => child.to === location.pathname))
                );
                
                return (
                  <AccordionItem
                    key={category.label}
                    aria-label={category.label}
                    title={
                      <div className="flex items-center gap-2 py-1">
                        {React.cloneElement(category.icon, { 
                          size: 20,
                          weight: isCategoryActive ? 'bold' : 'duotone',
                          className: isCategoryActive ? 'text-brick-primary' : 'text-foreground'
                        })}
                        <span className={`text-base font-medium ${isCategoryActive ? 'text-brick-primary' : 'text-foreground'}`}>
                          {category.label}
                        </span>
                      </div>
                    }
                    classNames={{
                      base: "border-b border-muted-foreground/50",
                      title: "text-base",
                      trigger: "py-3 px-4",
                      content: "pb-2"
                    }}
                  >
                    <div className="flex flex-col">
                      {category.routes.map((route) => {
                        // Check if this route or any of its children are active
                        const isRouteActive = location.pathname === route.to;
                        const hasActiveChild = route.children?.some(child => location.pathname === child.to);
                        
                        return (
                          <React.Fragment key={route.to}>
                            {/* Main route */}
                            <Link 
                              to={route.to} 
                              onClick={() => setIsMenuOpen(false)}
                              className={`flex items-center gap-2 px-6 py-2.5 transition-colors ${
                                isRouteActive || hasActiveChild
                                  ? 'bg-brick-primary/10 text-brick-primary' 
                                  : 'hover:bg-gray-50 text-foreground'
                              }`}
                            >
                              {React.cloneElement(route.icon, { 
                                size: 18,
                                weight: isRouteActive || hasActiveChild ? 'bold' : 'duotone' 
                              })}
                              <span className="text-sm font-medium">{route.label}</span>
                            </Link>
                            
                            {/* Child routes */}
                            {route.children && route.children.map((child) => (
                              <Link
                                key={child.to}
                                to={child.to} 
                                onClick={() => setIsMenuOpen(false)}
                                className={`flex items-center gap-2 px-10 py-2 transition-colors ${
                                  location.pathname === child.to 
                                    ? 'bg-brick-primary/10 text-brick-primary' 
                                    : 'hover:bg-gray-50 text-gray-600'
                                }`}
                              >
                                {React.cloneElement(child.icon, { 
                                  size: 16,
                                  weight: location.pathname === child.to ? 'bold' : 'duotone' 
                                })}
                                <span className="text-sm">{child.label}</span>
                              </Link>
                            ))}
                          </React.Fragment>
                        );
                      })}
                    </div>
                  </AccordionItem>
                );
              })}
            </Accordion>
            
            {/* Render standalone routes */}
            <div className="mt-2">
              {filteredStandaloneRoutes.map((route) => (
                <div key={route.to} className="border-b border-neutral-primary/10">
                  {/* Main route */}
                  <Link 
                    to={route.to} 
                    onClick={() => setIsMenuOpen(false)}
                    className={`flex items-center gap-2 px-4 py-3 transition-colors ${
                      location.pathname === route.to 
                        ? 'bg-brick-primary/10 text-brick-primary' 
                        : 'hover:bg-gray-50 text-foreground'
                    }`}
                  >
                    {React.cloneElement(route.icon, { 
                      size: 20,
                      weight: location.pathname === route.to ? 'bold' : 'duotone' 
                    })}
                    <span className="text-base font-medium">{route.label}</span>
                  </Link>
                  
                  {/* Child routes for standalone items */}
                  {route.children && route.children.map((child) => (
                    <Link
                      key={child.to}
                      to={child.to} 
                      onClick={() => setIsMenuOpen(false)}
                      className={`flex items-center gap-2 px-8 py-2 transition-colors ${
                        location.pathname === child.to 
                          ? 'text-brick-primary' 
                          : 'text-foreground'
                      }`}
                    >
                      {React.cloneElement(child.icon, { 
                        size: 16,
                        weight: location.pathname === child.to ? 'bold' : 'duotone' 
                      })}
                      <span className="text-sm">{child.label}</span>
                    </Link>
                  ))}
                </div>
              ))}
            </div>
          </div>
        </NavbarMenu>
      </Navbar>
      {isSearchActive && (
        <Navbar
          position="sticky"
          classNames={{
            base: "fixed mt-16 bg-muted shadow-md backdrop-blur-lg",
            wrapper: "px-3",
          }}
        >
          <div className="w-full">
            <SearchComponent type="mobile" onMobileNavSelect={handleSelect} />
          </div>
        </Navbar>
      )}
      
      {/* Subitems Navigation Bar for Mobile - Show when in a category or on a route with children */}
      {currentSubnavConfig && (
        <div className="fixed top-16 left-0 right-0 z-40 bg-muted border-b border-muted-foreground/50 px-5 py-3 shadow-sm">
          <div className="flex gap-3 overflow-x-auto">
            {currentSubnavConfig.type === 'category' ? (
              // Show all routes in the category
              currentSubnavConfig.routes.map((route) => {
                const isActive = matchesRoute(route.to, location.pathname) || 
                  (route.children ? route.children.some(child => matchesRoute(child.to, location.pathname)) : false);
                
                return (
                  <Link 
                    key={route.to}
                    to={route.to}
                    className={getMobileSubmenuLinkStyles(isActive)}
                  >
                    {React.cloneElement(route.icon, { 
                      weight: isActive ? 'bold' : 'duotone' 
                    })}
                    {route.label}
                  </Link>
                );
              })
            ) : (
              // Show route with its children (original behavior)
              <>
                {/* Parent route link - only show if showParentInSubmenu is not false */}
                {currentSubnavConfig.route.showParentInSubmenu !== false && (
                  <Link 
                    to={currentSubnavConfig.route.to}
                    className={getMobileSubmenuLinkStyles(matchesRoute(currentSubnavConfig.route.to, location.pathname))}
                  >
                    {React.cloneElement(currentSubnavConfig.route.icon, { 
                      weight: matchesRoute(currentSubnavConfig.route.to, location.pathname) ? 'bold' : 'duotone' 
                    })}
                    {currentSubnavConfig.route.label}
                  </Link>
                )}
                
                {/* Child route links */}
                {currentSubnavConfig.route.children?.map((child) => {
                  // Replace route parameters with actual values from current URL
                  let childPath = child.to;
                  const currentParts = location.pathname.split('/');
                  const routeParts = child.to.split('/');
                  
                  routeParts.forEach((part, index) => {
                    if (part.startsWith(':') && currentParts[index]) {
                      childPath = childPath.replace(part, currentParts[index]);
                    }
                  });
                  
                  const isActive = matchesRoute(child.to, location.pathname);
                  
                  return (
                    <Link 
                      key={child.to}
                      to={childPath}
                      className={getMobileSubmenuLinkStyles(isActive)}
                    >
                      {React.cloneElement(child.icon, { 
                        weight: isActive ? 'bold' : 'duotone' 
                      })}
                      {child.label}
                    </Link>
                  );
                })}
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
};
